import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { Fragment } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { useIsMediumDesktopAndAbove } from '@sorare/core/src/hooks/device/useIsMediumDesktopAndAbove';
import { glossary } from '@sorare/core/src/lib/glossary';

import { Blog } from 'components/home/Blog';
import { CommonMarket } from 'components/home/CommonMarket';
import { Referral } from 'components/home/Referral';
import { ScoutingCenterTile } from 'components/scoutingCenter/ScoutingCenterTile';

import { CompetitionHistory } from './CompetitionHistory';
import { KeepScouting } from './KeepScouting';
import type { ForYou_referral } from './__generated__/index.graphql';

const Divider = styled.div`
  height: 1px;
  width: 100%;
  background-color: var(--c-nd-100);
`;

const Layout = styled.div`
  display: grid;
  gap: var(--double-unit);
  justify-content: stretch;
  grid-template-columns: 1fr 1fr;
  align-items: stretch;

  & > * {
    width: 100%;
    height: 170px;
  }
  /* stretch last element if alone on line */
  & :nth-child(2n + 1):last-child {
    grid-column: 1/3;
  }
`;

type ForYouListItem =
  | 'commonMarket'
  | 'keepScouting'
  | 'scoutingCenter'
  | 'blog'
  | 'competitionHistory'
  | 'referral';

type Props = {
  sections: ForYouListItem[];
  referralConfig?: ForYou_referral;
};

export const ForYou = ({ sections, referralConfig }: Props) => {
  const isCompact = useIsMediumDesktopAndAbove();

  const renderSection = (
    section: ForYouListItem,
    { compact }: { compact?: boolean }
  ) => {
    if (section === 'commonMarket') {
      return <CommonMarket key={section} compact={compact} />;
    }
    if (section === 'keepScouting') {
      return <KeepScouting key={section} compact={compact} />;
    }
    if (section === 'scoutingCenter') {
      return <ScoutingCenterTile key={section} compact={compact} />;
    }
    if (section === 'blog') {
      return <Blog key={section} compact={compact} />;
    }
    if (section === 'competitionHistory') {
      return <CompetitionHistory key={section} compact={compact} />;
    }
    if (section === 'referral' && referralConfig) {
      return (
        <Referral key={section} compact={compact} config={referralConfig} />
      );
    }
    return null;
  };

  if (isCompact) {
    return (
      <Vertical gap={2}>
        <LabelL bold>
          <FormattedMessage {...glossary.forYou} />
        </LabelL>
        <Vertical gap={2}>
          {sections.map((section, i) => {
            const component = renderSection(section, { compact: true });
            if (i === sections.length - 1) {
              return component;
            }
            return (
              <Fragment key={section}>
                {component}
                <Divider />
              </Fragment>
            );
          })}
        </Vertical>
      </Vertical>
    );
  }

  return (
    <Vertical gap={2}>
      <LabelL bold>
        <FormattedMessage {...glossary.forYou} />
      </LabelL>
      <Layout>
        {sections.map(section => renderSection(section, { compact: false }))}
      </Layout>
    </Vertical>
  );
};

ForYou.fragments = {
  referral: gql`
    fragment ForYou_referral on Config {
      id
      ...Referral_config
    }
    ${Referral.fragments.config}
  ` as TypedDocumentNode<ForYou_referral>,
};
